import OctTags from '@/components/tags/OctTags.js'
import OctIconButton from '@/components/icon-button/OctIconButton.vue';
import OctButton from '@/components/button/OctButton.vue';
import OctCheckbox from '@/components/checkbox/OctCheckbox.vue';
import OctRadio from '@/components/radio/OctRadio';
import OctTextField from '@/components/text-field/OctTextField.vue';
import OctSelect from '@/components/select/OctSelect.vue';
import OctForm from '@/components/form/OctForm.vue';
import OctFormRow from '@/components/form/OctFormRow.vue';
import OctFormCol from '@/components/form/OctFormCol.vue';
import OctFormGroup from '@/components/form/OctFormGroup.vue';
import OctChipSet from '@/components/chips/OctChipSet.vue';
import OctChip from '@/components/chips/OctChip.vue';
import OctDialog from '@/components/dialog/OctDialog.vue';

/** Padder */
const pad = (num, d) => (Math.pow(10, d) + Number(num)).toString().substr(1, d)

/** Options for hour selection */
const hourOptions = Array(24).fill(0).reduce((acc, cur, i) => {
  const hour = `${pad(i, 2)}:00`

  // Push an hour
  acc.push({
    text: hour,
    value: `${hour}:00`,
  })

  return acc
}, [{
  text: 'None',
  value: '',
}])

const halfHourOptions = Array(24).fill(0).reduce((acc, cur, i) => {
  const hour = `${pad(i, 2)}:00`
  const half =  `${pad(i, 2)}:30`

  // Push an hour
  acc.push({
    text: hour,
    value: `${hour}:00`,
  })

  // Push a half hour
  acc.push({
    text: half,
    value: `${half}:00`,
  })

  return acc
}, [{
  text: 'None',
  value: '',
}])

/** Options for day selection */
const dayOptions = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Holiday'].reduce((acc, cur) => {
  // Push a day
  acc.push({
    text: cur,
    value: cur,
  })

  return acc
}, [{
  text: 'None',
  value: '',
}])

/** Month in English */
const monthsEng = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug','Sep', 'Oct', 'Nov', 'Dec']
const monthsEngFull = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August','September', 'October', 'November', 'December']

/** Categories */
const categories = OctTags

export default {
  name: 'OctPrototypeForm',
  components: {
    OctIconButton,
    OctButton,
    OctCheckbox,
    OctRadio,
    OctTextField,
    OctSelect,
    OctForm,
    OctFormRow,
    OctFormCol,
    OctFormGroup,
    OctChipSet,
    OctChip,
    OctDialog
  },
  props: {
    /** The user */
    user: {
      type: Object,
      default () {
        return {}
      }
    },
    /** Access token for Auth0 */
    token: {
      type: String,
      default: ''
    },
    /** Processing or not */
    processing: {
     type: Boolean,
     default: false
   }
  },
  data () {
    return {
      state: {
        dialog: '',
        processing: false
      },
      invalid: true,
      categories: categories,
      /** Options for day selection */
      $_octPrototypeForm_dayOptions: dayOptions,
      /** Options for hour selection */
      $_octPrototypeForm_hourOptions: hourOptions,
      /** Options for half hour selection */
      $_octPrototypeForm_halfHourOptions: halfHourOptions,
      /** Traget refs to validate */
      $_octPrototypeForm_validationTargets: [],
      /** Tags */
      $_octPrototypeForm_tags: this.tags
    }
  },
  computed: {
    /** Categoris by slug */
    categoriesBySlug () {
      return this.categories.reduce((acc, cur) => {
        acc[cur.slug] = cur
        return acc
      }, {})
    },
  },
  methods: {
    /** Confirm the data */
    async confirm () {
      // The result
      let invalid = false

      for (const item of this.$data.$_octPrototypeForm_validationTargets)
        if (item)
          invalid = !(await item.validate(item.value)) || invalid

      this.invalid = invalid

      return !invalid
    },
    /**
     * Submit
     * @event 'submit'
     */
    async submit () {
      // Call set up method
      this.beforeSubmit()

      try {
        // Check the data then submit
        await this.confirm() && this.$emit('submit')

        // Change form status
        this.invalid = true
      } catch (thrown) {
        alert('システムエラーが発生しました')
        console.log(thrown)
      }
    },
    /** Called before submit */
    beforeSubmit () {},
    /** Add an item to specified array */
    addItem (item, items=[]) {
      items.push(item)
    },
    /** Remove an item from specified array */
    removeItem (item, items=[]) {
      const index = items.indexOf(item)
      index !== -1 && items.splice(index, 1)
    },
    /** Close all dialogs */
    closeDialogs () {
      this.$set(this.state, 'dialog', '')
    },
    /** Convert tag into tag object */
    getTags () {
      return this.tags.reduce((acc, cur) => {
        // Get tag object and push tag if available
        const tag = this.categoriesBySlug[cur]
        tag && acc.push({text: tag.name})

        return acc
      }, [])
    },
    /** Convert date object into string */
    dateObjectToString (val) {
      // Create date and convert it
      return this.dateToString(new Date(val.year, val.month-1, val.date))
    },
    /** Convert date into string */
    dateToString (val, verbose=false) {
      // Ignore invalid date
      if (isNaN(val.getFullYear())) return ''

      return `${[...(verbose ? monthsEngFull : monthsEng)][val.getMonth()]} ${val.getDate()}, ${val.getFullYear()}`
    },
    /** Return locale string of specified property */
    getLocaleString (item, property) {
      item = item || this

      const en = item[`${property}EN`]
      const ja = item[property]

      return ja && ja || en
    },
    /**
     * Convert size object into string
     * @param {object} item - A size object
     * @param {string} unit - Unit of size description
     */
    sizeToString (item, unit='mm') {
      // Avoid invalid item
      if (!item) return ''

      // Ratio compare to cm scale
      const ratio = unit === 'mm' ? 1 : 1/25.4
      // Convert object into array
      const size = Object.values(item).reduce((acc, cur) => {
        cur && acc.push(new Intl.NumberFormat('en', {maximumFractionDigits:1}).format(cur*ratio))
        return acc
      }, [])

      // Avoid invalid item
      if (!size.length) return ''

      // Join size by '×' and add unit
      return `${size.join(' × ')} ${unit}`
    },
    /**
     * Convert period into string
     * @param {date} from - Period from
     * @param {date} to - Period to
     * @param {boolean} verbose - Whether to use long labels
     */
    periodToString (from, to, verbose=false) {
      // Output array or strings
      [from, to] = [from, to].map(item => {
        // Ignore invalid input
        if (!item || isNaN(item.getFullYear()) || item.getFullYear() === 1970)
          return ['', '', '']
        
        return [
          [...(verbose && monthsEngFull || monthsEng)][item.getMonth()] + ' ',
          this.pad(item.getDate(), 2),
          ', ' + item.getFullYear()
        ]
      })

      // Invalid period
      if (!from[0] && !to[0]) return ''

      // Organize output arrays
      from[2] === to[2] && from.pop()
      from[0] === to[0] && from.shift()

      return `${from.join('')} – ${to.join('')}`
    },
    /**
     * Analyze relationship between date and period
     * @param {date} from - From date of period to analyze
     * @param {date} to - To date of period to analyze
     * @param {date} date - A date to analyze
     * @return {number} 0: The day is in the period, 1: The day is after the period, -1: The day is before the period
     */
    analyzeDateAndPeriod (from, to, date=new Date) {
      // Normalize argumants
      [from, to, date] = [from, to, date].map((item, i) => {
        return ((!item || isNaN(item.getFullYear()) || item.getFullYear() === 1970) ?
          (i === 0 ? new Date(0) : new Date(3000, 0, 1)) : item).getTime()
      })

      // Compare date
      return date < from ? -1 : (date > to ? 1 : 0)
    },
    pad: pad
  },
  watch: {
    /** Watch props */
    $props: {
      handler () {
        // Wait for propagation and confirm
        this.$nextTick(() => {
          this.confirm()
        })
      },
      deep: true
    }
  },
}